import useClientValue from '@/utils/hooks/useClientValue'

interface Options {
  lang?: string
  continuous?: boolean
}

const useVoiceToText = ({lang, continuous}: Options = {lang: 'en-US', continuous: true}) => {
  const latestContinuousSetting = useRef<boolean>(continuous ?? true)
  const shouldContinueListening = useRef<boolean>(false)
  const speechRecognitionRef = useRef<SpeechRecognition | null>(null)

  const [transcript, setTranscript] = useState<string>('')

  const SpeechRecognition = useClientValue(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- window.SpeechRecognition is not always available
    return globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition
  })

  const isSpeechRecognitionAvailable = !!SpeechRecognition

  const startListening = useCallback(() => {
    if (!speechRecognitionRef.current) return
    try {
      speechRecognitionRef.current.start()
    } catch {
      // do nothing
    }
    shouldContinueListening.current = latestContinuousSetting.current
  }, [])

  const stopListening = useCallback(() => {
    if (!speechRecognitionRef.current) return
    try {
      speechRecognitionRef.current.stop()
    } catch {
      // do nothing
    }
    shouldContinueListening.current = false
  }, [])

  const reset = useCallback(() => {
    setTranscript('')
  }, [])

  useEffect(
    function initSpeechRecognition() {
      if (SpeechRecognition && !speechRecognitionRef.current) {
        speechRecognitionRef.current = new SpeechRecognition()
      }

      function onEnd() {
        if (shouldContinueListening.current) {
          // if the listening is continuous, it starts listening even the speaker is quiet till it will be stopped manually
          startListening()
        }
      }

      function onError(event: SpeechRecognitionErrorEvent) {
        console.error(`Speech recognition error detected: ${event.error}`)
      }

      function onResult(event: SpeechRecognitionEvent) {
        const transcript = event.results[0]?.[0]?.transcript
        if (!transcript) return
        setTranscript(prevTranscript =>
          prevTranscript ? `${prevTranscript} ${transcript}` : transcript,
        )
      }

      if (speechRecognitionRef.current) {
        if (lang) {
          speechRecognitionRef.current.lang = lang
        }

        speechRecognitionRef.current.addEventListener('end', onEnd)
        speechRecognitionRef.current.addEventListener('error', onError)
        speechRecognitionRef.current.addEventListener('result', onResult)
      }

      return () => {
        speechRecognitionRef.current?.removeEventListener('end', onEnd)
        speechRecognitionRef.current?.removeEventListener('error', onError)
        speechRecognitionRef.current?.removeEventListener('result', onResult)
        try {
          speechRecognitionRef.current?.abort()
        } catch {
          // do nothing
        }
        speechRecognitionRef.current = null
      }
    },
    [SpeechRecognition, lang, startListening, stopListening, reset],
  )

  return {
    startListening,
    stopListening,
    transcript,
    reset,
    isSpeechRecognitionAvailable,
  }
}
export default useVoiceToText
